/**
 * Permits List Page (Phase 9.16b)
 *
 * Standalone permit listing with:
 * - View tabs: Upcoming, Overdue, All
 * - Search and filter
 * - Sortable columns
 * - Pagination
 * - Urgency color coding
 */
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { ViewTabs, ViewTab } from '@/components/ui/view-tabs';
import { FilterBar } from '@/components/ui/filter-bar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Plus, ChevronUp, ChevronDown, ChevronsUpDown, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAllPermits, usePermitTypes } from '@/hooks/usePermits';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PaginationInfo } from '@/components/PaginationInfo';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { calculatePaginationValues } from '@/lib/pagination';
import { todayLocalDateString } from '@/lib/localDate';
import { format, differenceInDays, isAfter, parseISO } from 'date-fns';
import type { PermitWithProject, PermitStatus } from '@/types/permits';

// View tabs configuration
const permitViews: ViewTab[] = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'all', label: 'All' },
];

// Status badge colors
const STATUS_COLORS: Record<PermitStatus, string> = {
  not_submitted: 'bg-slate-100 text-slate-600',
  submitted: 'bg-sky-50 text-sky-700',
  issued: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-slate-100 text-slate-600',
};

const STATUS_LABELS: Record<PermitStatus, string> = {
  not_submitted: 'Not Submitted',
  submitted: 'Submitted',
  issued: 'Issued',
  closed: 'Closed',
};

/**
 * Get urgency class based on due date
 * Red: Overdue or due within 7 days
 * Orange: Due within 30 days
 * Green: Due in 30+ days or no due date
 */
function getUrgencyClass(dueDate: string | null | undefined): string {
  if (!dueDate) return '';

  const today = new Date();
  const due = parseISO(dueDate);
  const daysUntilDue = differenceInDays(due, today);

  if (daysUntilDue < 0) {
    return 'text-red-600 font-medium'; // Overdue
  } else if (daysUntilDue <= 7) {
    return 'text-red-500'; // Due within 7 days
  } else if (daysUntilDue <= 30) {
    return 'text-orange-500'; // Due within 30 days
  }
  return 'text-green-600'; // Due in 30+ days
}

/**
 * Get urgency badge for due date
 */
function DueDateBadge({ dueDate }: { dueDate: string | null | undefined }) {
  if (!dueDate) {
    return <span className="text-muted-foreground">-</span>;
  }

  const today = new Date();
  const due = parseISO(dueDate);
  const daysUntilDue = differenceInDays(due, today);

  let badgeClass = 'bg-emerald-50 text-emerald-700';
  let label = format(due, 'MMM d, yyyy');

  if (daysUntilDue < 0) {
    badgeClass = 'bg-rose-50 text-rose-700';
    label = `${Math.abs(daysUntilDue)}d overdue`;
  } else if (daysUntilDue === 0) {
    badgeClass = 'bg-rose-50 text-rose-700';
    label = 'Due today';
  } else if (daysUntilDue <= 7) {
    badgeClass = 'bg-rose-50 text-rose-700';
    label = `${daysUntilDue}d left`;
  } else if (daysUntilDue <= 30) {
    badgeClass = 'bg-amber-50 text-amber-700';
    label = format(due, 'MMM d');
  }

  return <Badge variant="secondary" className={cn('font-normal', badgeClass)}>{label}</Badge>;
}

export function PermitsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL params
  const currentView = searchParams.get('view') || 'all';
  const search = searchParams.get('search') || '';
  const sortBy = searchParams.get('sort') || 'due_date';
  const sortOrder = searchParams.get('order') || 'asc';

  // 9.16b: Type and status filters
  const permitTypeFilter = searchParams.get('type') || '';
  const statusFilter = searchParams.get('status') || '';

  // Fetch permit types for filter dropdown
  const { data: permitTypesData } = usePermitTypes();
  const permitTypes = permitTypesData?.permit_types || [];

  // Pagination state management in URL
  const { page, limit, offset, setPage } = usePaginationState(50);

  // Determine date filters based on current view
  let dueAfter: string | undefined;
  let dueBefore: string | undefined;
  const today = todayLocalDateString();

  switch (currentView) {
    case 'upcoming':
      dueAfter = today; // Due today or later
      break;
    case 'overdue':
      dueBefore = today; // Due before today
      break;
    // 'all': no date filter
  }

  // Fetch permits with backend filtering and pagination
  const { data, isLoading, error } = useAllPermits({
    search: search || undefined,
    dueAfter,
    dueBefore,
    permitTypeDefinitionId: permitTypeFilter || undefined,
    status: statusFilter as PermitStatus | undefined,
    sortBy: sortBy as 'due_date' | 'created_at' | 'permit_type' | 'project_number',
    sortOrder: sortOrder as 'asc' | 'desc',
    limit,
    offset,
  });

  const permits = data?.permits || [];
  const total = data?.total || 0;

  // Calculate pagination values
  const paginationValues = calculatePaginationValues(total, offset, limit);

  // Handle view tab change - reset to page 1
  const handleViewChange = (view: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (view === 'all') {
      newParams.delete('view');
    } else {
      newParams.set('view', view);
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle search change - reset to page 1
  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // 9.16b: Handle permit type filter change
  const handleTypeFilterChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('type', value);
    } else {
      newParams.delete('type');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // 9.16b: Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('status', value);
    } else {
      newParams.delete('status');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle sort change (clicking column headers)
  const handleSort = (column: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (sortBy === column) {
      // Toggle order if same column
      newParams.set('order', sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to asc for dates
      newParams.set('sort', column);
      newParams.set('order', column === 'due_date' ? 'asc' : 'desc');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle clear all filters
  const handleClearAll = () => {
    setSearchParams(new URLSearchParams());
  };

  // Navigate to project detail page with permit section
  const handleRowClick = (permit: PermitWithProject) => {
    navigate(`/projects/${permit.project_id}?section=permits&permit=${permit.id}`);
  };

  // Sortable header component
  const SortableHeader = ({
    column,
    label,
    className,
  }: {
    column: string;
    label: string;
    className?: string;
  }) => {
    const isActive = sortBy === column;
    return (
      <TableHead
        className={cn("cursor-pointer select-none hover:bg-muted/50", className)}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive ? (
            sortOrder === 'asc' ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )
          ) : (
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground/50" />
          )}
        </div>
      </TableHead>
    );
  };

  if (isLoading && permits.length === 0) {
    return (
      <DashboardLayout title="Permits & Submissions">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Permits & Submissions"
      actions={
        <Button onClick={() => navigate('/projects?action=add-permit')}>
          <Plus className="mr-2 h-4 w-4" />
          New Permit / Submission
        </Button>
      }
    >
      <div className="mb-4">
        <p className="text-muted-foreground">
          Track permits, submissions, and internal milestones across all projects
        </p>
      </div>

      {/* View Tabs */}
      <ViewTabs
        views={permitViews}
        currentView={currentView}
        onChange={handleViewChange}
      />

      {/* Filter Bar */}
      <FilterBar
        search={search}
        onSearchChange={handleSearchChange}
        placeholder="Search permits..."
        onClearAll={handleClearAll}
        activeFilterCount={(search ? 1 : 0) + (permitTypeFilter ? 1 : 0) + (statusFilter ? 1 : 0)}
        advancedFilters={
          <>
            {/* 9.16b: Permit Type Filter */}
            <Select value={permitTypeFilter || 'all'} onValueChange={handleTypeFilterChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Permit Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {permitTypes.map((pt) => (
                  <SelectItem key={pt.id} value={pt.id}>
                    {pt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* 9.16b: Status Filter */}
            <Select value={statusFilter || 'all'} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="not_submitted">Not Submitted</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
      />

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load permits: {(error as Error).message}
          </p>
        </div>
      )}

      {/* Table */}
      {!error && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="permit_type" label="Type" />
                  <SortableHeader column="project_number" label="Project" />
                  <TableHead>Client</TableHead>
                  <SortableHeader column="due_date" label="Due Date" />
                  <TableHead>Status</TableHead>
                  <TableHead>Case #</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {permits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center h-32">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <p>No permits found</p>
                        {search && (
                          <p className="text-sm mt-1">
                            Try adjusting your search terms
                          </p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  permits.map((permit) => (
                    <TableRow
                      key={permit.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(permit)}
                    >
                      <TableCell className="font-medium">
                        {permit.permit_type_definition_name || permit.permit_type}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            #{permit.project_number}
                          </span>
                          <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {permit.project_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {permit.client_name || '-'}
                      </TableCell>
                      <TableCell>
                        <DueDateBadge dueDate={permit.due_date} />
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={cn('font-normal', STATUS_COLORS[permit.display_status])}
                        >
                          {STATUS_LABELS[permit.display_status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {permit.case_number || '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/projects/${permit.project_id}?section=permits`);
                          }}
                          title="View project"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="mt-4 flex items-center justify-between">
              <PaginationInfo
                currentPage={page}
                pageSize={limit}
                totalItems={total}
              />
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage(Math.max(1, page - 1))}
                      className={cn(page === 1 && 'pointer-events-none opacity-50')}
                    />
                  </PaginationItem>
                  {paginationValues.pages.map((pageNum, index) => (
                    <PaginationItem key={index}>
                      {pageNum === '...' ? (
                        <PaginationEllipsis />
                      ) : (
                        <PaginationLink
                          onClick={() => setPage(pageNum as number)}
                          isActive={pageNum === page}
                        >
                          {pageNum}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setPage(Math.min(paginationValues.totalPages, page + 1))}
                      className={cn(page === paginationValues.totalPages && 'pointer-events-none opacity-50')}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}
